import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { toCheckoutData } from '@leanpay/models'
import {
    checkoutService,
    getActiveCheckoutToken,
    isStandardErrorResponse,
    logoutUser,
    setActiveCheckoutToken,
} from '@leanpay/services'

import { useAppStore } from './app-store'
import { useGetSessionInfo, useGetVendorTokenCheckout } from 'library/hooks/query-hooks'
import { useRouteTracker } from 'library/hooks/use-step-tracker'
import { isStandalonePublicRoute } from 'library/utils'

const AppStore = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate()
    const { data: sessionInfoData } = useGetSessionInfo()
    const { mutateAsync: mutateGetVendorTokenCheckout } = useGetVendorTokenCheckout()
    const params = new URLSearchParams(window.location.search)
    const location = useLocation()

    // Zustand setters
    const {
        setVendorToken,
        setCheckoutData,
        setIsAuthenticated,
        lastRecordedRoute,
        canAccessRoute,
        resetStore,
        vendorToken,
    } = useAppStore()
    const isInitialLoad = useRef(true)

    // Track route changes
    useRouteTracker()

    const handleGetVendorToken = async () => {
        const token = params.get('token')

        if (token) {
            // The auth token lives in localStorage, shared by every tab of this origin,
            // while this store is per-tab (sessionStorage). Clearing it unconditionally
            // means re-opening the checkout link - a second tab, or clicking "Pay with
            // Leanpay" again - logs the customer out of the tab they are actually using,
            // and the loss only surfaces later at the first authenticated call. Only a
            // different checkout may end the existing session.
            if (getActiveCheckoutToken() !== token) {
                logoutUser()
                setActiveCheckoutToken(token)
            }

            resetStore()
            try {
                const vendorInitApiData = await mutateGetVendorTokenCheckout(token)

                setVendorToken(vendorInitApiData.token)

                setCheckoutData(
                    toCheckoutData({
                        token: vendorInitApiData.token,
                        isInStore: vendorInitApiData.inStoreFlag,
                        latestConsentsAccepted: vendorInitApiData.latestConsentsAccepted,
                        vendorRequestType: vendorInitApiData.vendorRequestType,
                        customerLanguage: vendorInitApiData.customerLanguage,
                        vendorName: vendorInitApiData.vendorName,
                        graceEnabled: vendorInitApiData.graceEnabled,
                        insuranceRatePercent: vendorInitApiData.insuranceRatePercent,
                        rbpEnabled: vendorInitApiData.rbpEnabled,
                        upsellEnabled: vendorInitApiData.upsellEnabled,
                    }),
                )

                navigate('/choose-installment', { replace: true })
            } catch (error) {
                const errorMessage = isStandardErrorResponse(error) ? error.message : error
                navigate('/checkout-error', {
                    state: { error: errorMessage },
                    replace: true,
                })
            }
        } else {
            navigate('/checkout-error', {
                state: { error: 'DATA_NOT_VALID' },
                replace: true,
            })
        }
    }

    useEffect(() => {
        if (location.pathname === '/' && !isStandalonePublicRoute) {
            handleGetVendorToken()
        }
    }, [location.pathname])

    useEffect(() => {
        if (sessionInfoData) {
            setIsAuthenticated(true)
        }
    }, [sessionInfoData])

    // Route validation on initial load/refresh
    useEffect(() => {
        if (isInitialLoad.current && lastRecordedRoute && location.pathname !== '/') {
            isInitialLoad.current = false

            // If current route !== last recorded route, kick them out
            if (!canAccessRoute(location.pathname)) {
                const handleInvalidAccess = async () => {
                    try {
                        if (vendorToken) {
                            await checkoutService.invalidateVendorRequest({ token: vendorToken })
                        }
                    } catch {
                        console.error()
                    } finally {
                        resetStore()
                        logoutUser()
                        navigate('/checkout-error', {
                            state: { error: 'DATA_NOT_VALID' },
                            replace: true,
                        })
                    }
                }
                void handleInvalidAccess()
            }
        }
    }, [location.pathname, lastRecordedRoute, canAccessRoute, vendorToken, resetStore, navigate])

    return children
}

export default AppStore
